import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import ImageBase64Tool from '../components/ImageBase64Tool';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I convert an image to Base64?', a: 'Choose an image above and the tool instantly produces a Base64 data URI you can copy. Everything runs in your browser, so the image is never uploaded.' },
  { q: 'What is a data URI?', a: 'A data URI embeds a file directly in text using the form data:[mime];base64,[data]. You can paste it into an <img src>, CSS background, or any place that accepts a URL.' },
  { q: 'Should I Base64-encode large images?', a: 'Only small ones. Base64 increases size by ~33% and inlined images can’t be cached separately, so reserve it for tiny icons/sprites. Larger images are better served as normal files.' },
  { q: 'Which image formats are supported?', a: 'Any format your browser can read — PNG, JPEG, GIF, WebP, SVG and more. The resulting data URI keeps the original MIME type.' },
];

export default function ImageToBase64Page() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="Image to Base64 Converter — Encode Images Online | EasyBase64"
        description="Convert an image to a Base64 data URI online for free. Drag in a PNG, JPG, GIF, SVG or WebP and copy the encoded string. Private — runs entirely in your browser."
        path="/image-to-base64"
        jsonLd={[
          softwareApplicationSchema({ name: 'Image to Base64 Converter', description: 'Convert images to Base64 data URIs in the browser.', path: '/image-to-base64' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Image to Base64', path: '/image-to-base64' }]),
        ]}
      />
      <PageHero
        title="Image to Base64"
        subtitle={<>Turn any image into a Base64 data URI you can embed in HTML or CSS. Need the reverse? Use <Link to="/base64-to-image" className="underline">Base64 to Image</Link>.</>}
      />
      <div className="mb-12">
        <ImageBase64Tool direction="encode" />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>How to use the Base64 data URI</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed`}>
          Copy the generated string and drop it straight into an image tag's <code>src</code> attribute or a CSS <code>background-image</code>. Because the data is inline, the browser renders it without a separate network request — handy for tiny icons, but avoid it for large images (see the FAQ).
        </p>
      </section>
      <FaqSection title="Image to Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
